/**
 * FundraiserDonationsTab.tsx - Donations Management Tab
 *
 * This component provides comprehensive donation management including
 * transaction history, refund processing, and donation analytics.
 *
 * Key Features:
 * - Complete donation transaction history
 * - Donation details with expandable information
 * - Refund processing and management
 * - Payment method breakdown and analytics
 * - Sorting and filtering of donations
 * - Export capabilities for donation data
 *
 * Donation Information:
 * - Donor name and contact information
 * - Donation amount and currency
 * - Payment method with masked details
 * - Transaction timestamp and ID
 * - Promoter attribution (if applicable)
 * - Donation comments and messages
 *
 * Management Actions:
 * - View detailed donation information
 * - Process refunds with reason tracking
 * - Export donation data for reporting
 * - Contact donors for follow-up
 * - Manage recurring donations
 *
 * Analytics Features:
 * - Payment method breakdown (card vs ACH)
 * - Average donation calculations
 * - Donation frequency analysis
 * - Promoter attribution tracking
 *
 * Props: None (uses MobX store for donation data)
 *
 * Dependencies: DonationDetailModal, RefundModal, donation utilities
 */

import React, { act, useState } from "react";
import styled from "styled-components";
import {
  X,
  ChevronUp,
  ChevronDown,
  Eye,
  RotateCcw,
  CreditCard,
  CaseSensitive as University,
  DollarSign,
  XCircle,
  RefreshCcw,
} from "lucide-react";
import { DonationDetailModal } from "../DonationDetailModal";
import { RefundModal } from "../RefundModal";
import { ViewSubscriptionModal } from "../ViewSubscriptionModal";
import { CancelSubscriptionModal } from "../CancelSubscriptionModal";
import {
  SearchWrapper,
  SearchInput,
  ClearButton,
  DataTable,
  TableHeader,
  TableRow,
  NoResults,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  DonationsSegmentControl,
  DonationsSegmentButton,
  StatusBadge,
} from "../shared/StyledComponents";
import { IDonation, ISubscription } from "../../interfaces";
import {
  FundraisersDonationsBoardResponse,
  SubscriptionModel,
} from "../../models";
import {
  CancelSubscriptionTypes,
  ExportDropDown,
  ExportFormat,
  IAPICancelSubscriptionModel,
  RefundSubscriptionTypes,
  RenderPaymentMethod,
  SubscriptionStatus,
  downloadBlob,
  exportFileName,
  isUsableExportFile,
  showExportError,
} from "@mindmemobile/mm-payment-modules";
import { fundraiserStore } from "../../stores";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../../services";
import { AuthService, ToastService } from "@mindmemobile/mm-shared-global";
import { canManageFundraiser } from "../../utils/permissions";

const TopDonationsContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 32px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const TopDonationsTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TopDonationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DonationCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 20px;
  min-height: 100px;
  min-width: 280px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const DonationAvatar = styled.div<{ isAnonymous?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: ${(props) => (props.isAnonymous ? "flex" : "flex")};
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DonationInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DonationName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DonationDate = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const DonationAmount = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #10b981;
  text-align: right;
  flex-shrink: 0;
`;

const SectionHeaderWithSearch = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 32px;
  gap: 24px;
`;

const SectionHeaderCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const HeaderSegmentControl = styled.div`
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(16px);
  border-radius: 16px;
  padding: 6px;
  display: flex;
  gap: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.5);
`;

const HeaderSegmentButton = styled.button<{ active: boolean }>`
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  ${(props) =>
    props.active
      ? `
    background: #1e293b;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(30, 41, 59, 0.3);
    transform: translateY(-1px);
  `
      : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      background: rgba(30, 41, 59, 0.08);
      color: #1e293b;
    }
  `}
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #10b981;
  }
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.6;
`;

const DonorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DonorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DonorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DonorName = styled.div`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  margin-bottom: 4px;
`;

const ContactItem = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AmountValue = styled.div`
  font-weight: 800;
  color: #1e293b;
  font-size: 16px;
  text-align: right;
`;

const DateTimeValue = styled.div`
  font-size: 16px;
  color: #1e293b;
  font-weight: normal;
`;

const RecurringTooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const RecurringTooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const RecurringIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RecurringIcon = styled.img`
  width: 15px;
  height: 15px;
  filter: brightness(0) saturate(100%) invert(36%) sepia(8%) saturate(572%)
    hue-rotate(202deg) brightness(91%) contrast(86%);
`;

const PaymentMethodContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentMethodIcon = styled.div`
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  padding: 4px;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
  }
`;

const PaymentAccountNumber = styled.span`
  font-size: 16px;
  color: #1e293b;
  font-weight: 500;
`;

const ActionIcons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 12px 24px 20px;
  min-height: 200px;
`;

const PlaceholderText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

type SortField =
  | "name"
  | "donation"
  | "date"
  | "recurring"
  | "nextDue"
  | "paid"
  | null;
type SortDirection = "asc" | "desc";
type TabType = "one-time" | "recurring";

interface FundraiserDonationsTabProps {
  donations: FundraisersDonationsBoardResponse[];
  subscriptions: SubscriptionModel[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onCloseRefund?: (refresh?: boolean) => void;
}

export const FundraiserDonationsTab: React.FC<FundraiserDonationsTabProps> = ({
  donations,
  subscriptions,
  searchTerm,
  onSearchChange,
  onCloseRefund,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("one-time");
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDonation, setSelectedDonation] =
    useState<FundraisersDonationsBoardResponse | null>(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundDonation, setRefundDonation] =
    useState<FundraisersDonationsBoardResponse | null>(null);
  const [showViewSubscriptionModal, setShowViewSubscriptionModal] =
    useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] =
    useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionModel | null>(null);
  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null);
  const [hoveredRecurring, setHoveredRecurring] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  /* Exports whichever grid is showing (One Time or Recurring). The API builds the file from the
     same query that fills the tab, honouring the current search box. */
  const exportGrid = (format: ExportFormat) => {
    if (isExporting) return;
    if (!canManageFundraiser("Export")) return;
    const recurring = activeTab === "recurring";
    setIsExporting(true);
    FundraiserService.exportDonations(
      AuthService.authData.accountId,
      fundraiserStore.selectedFundraiserId || "",
      recurring,
      format,
      searchTerm
    ).subscribe({
      next: (blob) => {
        // Cleared before the download so a browser that refuses it cannot strand the button.
        setIsExporting(false);
        if (!isUsableExportFile(blob)) {
          ToastService.showError("Error in exporting donations");
          return;
        }
        downloadBlob(
          blob,
          exportFileName(
            recurring ? "fundraiser-recurring-donations" : "fundraiser-donations",
            format
          )
        );
      },
      error: (error) => {
        setIsExporting(false);
        showExportError(error, "Error in exporting donations");
      },
    });
  };

  // Use subscriptions from props instead of local state
  // const [subscriptions, setSubscriptions] = useState<SubscriptionModel[]>(
  //   initialSubscriptionsData
  // );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  const formatDateOnly = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const getInitials = (name: string) => {
    if (!name || name === "Anonymous Donor") {
      return "AD";
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const getContactInfo = (donation: FundraisersDonationsBoardResponse) => {
    // Always show contact info in admin views
    const contacts: string[] = [];
    if (donation.donorEmail) contacts.push(donation.donorEmail);
    if (donation.donorMobile) contacts.push(donation.donorMobile.phoneNumber);
    return contacts;
  };

  const getSubscriptionContactInfo = (subscription: SubscriptionModel) => {
    const contacts: string[] = [];
    if (subscription.donorEmail) contacts.push(subscription.donorEmail);
    if (subscription.donorMobile) contacts.push(subscription.donorMobile);
    return contacts;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "date" ? "desc" : "asc");
    }
  };

  const clearSearch = () => {
    onSearchChange("");
  };

  const handleViewDetails = (donation: FundraisersDonationsBoardResponse) => {
    setSelectedDonation(donation);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDonation(null);
  };

  const handleRefund = (donation: FundraisersDonationsBoardResponse) => {
    setRefundDonation(donation);
    setShowRefundModal(true);
  };

  const handleCloseRefundModal = (refresh?: boolean) => {
    setShowRefundModal(false);
    setRefundDonation(null);
    if (refresh && onCloseRefund) {
      onCloseRefund(true);
    }
  };

  const handleViewSubscription = (subscription: SubscriptionModel) => {
    setSelectedSubscription(subscription);
    setShowViewSubscriptionModal(true);
  };

  const handleCloseViewSubscriptionModal = () => {
    setShowViewSubscriptionModal(false);
    setSelectedSubscription(null);
  };

  const handleCancelSubscription = (subscription: SubscriptionModel) => {
    setSelectedSubscription(subscription);
    setShowCancelSubscriptionModal(true);
  };

  const handleCloseCancelSubscriptionModal = () => {
    setShowCancelSubscriptionModal(false);
    setSelectedSubscription(null);
  };

  const handleConfirmCancelSubscription = () => {
    onCloseRefund && onCloseRefund(true);
    handleCloseCancelSubscriptionModal();
  };

  const filteredDonations = donations.filter((donation) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      donation.donorName.toLowerCase().includes(searchLower) ||
      (donation.donorEmail &&
        donation.donorEmail.toLowerCase().includes(searchLower)) ||
      (donation.donorMobile &&
        donation.donorMobile.phoneNumber.includes(searchTerm)) ||
      (donation.comment && donation.comment.toLowerCase().includes(searchLower))
    );
  });

  const filteredSubscriptions = subscriptions.filter((subscription) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      subscription.donorName.toLowerCase().includes(searchLower) ||
      (subscription.donorEmail &&
        subscription.donorEmail.toLowerCase().includes(searchLower)) ||
      (subscription.donorMobile &&
        subscription.donorMobile.includes(searchTerm))
    );
  });

  const getSortedDonations = () => {
    let sorted = [...filteredDonations];
    if (sortField === "name") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.donorName
          .toLowerCase()
          .localeCompare(b.donorName.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "donation") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.amount - b.amount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "date") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.timestamp.getTime() - b.timestamp.getTime();
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return sorted;
  };

  const getSortedSubscriptions = () => {
    let sorted = [...filteredSubscriptions];

    if (sortField === "name") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.donorName
          .toLowerCase()
          .localeCompare(b.donorName.toLowerCase());
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "recurring") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.monthlyAmount - b.monthlyAmount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "nextDue") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.nextDueDate.getTime() - b.nextDueDate.getTime();
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "paid") {
      sorted = sorted.sort((a, b) => {
        const comparison = a.totalPaid - b.totalPaid;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return sorted;
  };

  // Calculate top donations by amount
  const getTopDonations = () => {
    return donations
      .slice()
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <SortIcon>
          <ChevronUp size={12} />
          <ChevronDown size={12} style={{ marginTop: "-2px" }} />
        </SortIcon>
      );
    }

    return (
      <SortIcon style={{ opacity: 1 }}>
        {sortDirection === "asc" ? (
          <ChevronUp size={16} />
        ) : (
          <ChevronDown size={16} />
        )}
      </SortIcon>
    );
  };

  const getPaymentMethodIcon = (paymentMethod: IDonation["paymentMethod"]) => {
    if (paymentMethod.type === "card") {
      const brand = paymentMethod.brand?.toLowerCase();
      switch (brand) {
        case "visa":
          return "/visa-icon.svg";
        case "mastercard":
          return "/mastercard-icon.svg";
        case "amex":
          return "/amex-icon.svg";
        case "discover":
          return "/discover-icon.svg";
        default:
          return "/visa-icon.svg";
      }
    } else {
      return "/bank-icon.svg";
    }
  };

  // const renderPaymentMethod = (paymentMethod: IDonation['paymentMethod']) => {
  //   const iconSrc = getPaymentMethodIcon(paymentMethod);

  //   return (
  //     <PaymentMethodContainer>
  //       <PaymentMethodIcon>
  //         <img
  //           src={iconSrc}
  //           alt={paymentMethod.type === 'card' ? paymentMethod.brand : 'Bank'}
  //         />
  //       </PaymentMethodIcon>
  //       <PaymentAccountNumber>
  //         ...{paymentMethod.type === 'card' ? paymentMethod.last4 : paymentMethod.last6}
  //       </PaymentAccountNumber>
  //     </PaymentMethodContainer>
  //   );
  // };

  const sortedDonations = getSortedDonations();
  const sortedSubscriptions = getSortedSubscriptions();

  // Helper function to determine if a donation is monthly (mock logic for demo)
  const isMonthlyDonation = (donation: FundraisersDonationsBoardResponse) => {
    // // Check if donation has isMonthly flag or if donor name contains "Jeff Cohen"
    // if ((donation as any).isMonthly) {
    //   return true;

    return donation.isMonthly;
    // }
    // const displayName = getDisplayName(donation);
    // if (displayName.toLowerCase().includes('jeff cohen')) {
    //   return true;
    // }
    // // For demonstration, make every 4th donation monthly based on donation ID
    // return parseInt(donation.id) % 4 === 0;
  };

  // Check if we have any donations to show content
  const hasNoDonations = donations.length === 0;

  const topDonations = getTopDonations();

  function renderGridsData() {
    if (activeTab === "one-time") {
      if (sortedDonations.length === 0) {
        return (
          <NoResults>
            {searchTerm
              ? `No donations found matching "${searchTerm}"`
              : "No donations found"}
          </NoResults>
        );
      }
      return sortedDonations.map((donation) => (
        <TableRow
          key={donation.id}
          columns="2.3fr 0.7fr 0.3fr 1.5fr 1.6fr 120px"
        >
          <DonorInfo>
            <DonorAvatar>{getInitials(donation.getDisplayName())}</DonorAvatar>

            <DonorDetails>
              <DonorName>{donation.getDisplayName()}</DonorName>
              {!donation.isAnonymous && (
                <ContactInfo>
                  {getContactInfo(donation).map((contact, index) => (
                    <ContactItem key={index}>{contact}</ContactItem>
                  ))}
                </ContactInfo>
              )}
            </DonorDetails>
          </DonorInfo>

          <AmountValue>{formatCurrency(donation.amount)}</AmountValue>

          <RecurringIconContainer>
            {isMonthlyDonation(donation) && (
              <RecurringTooltipContainer>
                <RecurringIcon
                  src="/recurring.svg"
                  alt="Monthly donation"
                  onMouseEnter={() => setHoveredRecurring(donation.id)}
                  onMouseLeave={() => setHoveredRecurring(null)}
                  style={{ cursor: "help" }}
                />
                <RecurringTooltip visible={hoveredRecurring === donation.id}>
                  Monthly donation
                </RecurringTooltip>
              </RecurringTooltipContainer>
            )}
          </RecurringIconContainer>

          <DateTimeValue style={{ marginLeft: "16px" }}>
            {formatDate(donation.donatedOn)}
          </DateTimeValue>

          <div style={{ marginLeft: "40px", textAlign: "left" }}>
            {/* {renderPaymentMethod(donation.paymentMethod)} */}
            <RenderPaymentMethod paymentMethod={donation.paymentMethod} />
          </div>

          <ActionIcons>
            <TooltipContainer>
              <IconButton
                onClick={() => handleViewDetails(donation)}
                onMouseEnter={() => setHoveredTooltip(`view-${donation.id}`)}
                onMouseLeave={() => setHoveredTooltip(null)}
              >
                <Eye size={14} />
              </IconButton>
              <Tooltip visible={hoveredTooltip === `view-${donation.id}`}>
                View
              </Tooltip>
            </TooltipContainer>
            <TooltipContainer>
              <IconButton
                onClick={() => handleRefund(donation)}
                onMouseEnter={() => setHoveredTooltip(`refund-${donation.id}`)}
                onMouseLeave={() => setHoveredTooltip(null)}
                disabled={donation.amount <= 0}
              >
                <RotateCcw size={14} />
              </IconButton>
              <Tooltip visible={hoveredTooltip === `refund-${donation.id}`}>
                Refund
              </Tooltip>
            </TooltipContainer>
          </ActionIcons>
        </TableRow>
      ));
    } else {
      if (sortedSubscriptions.length === 0) {
        return (
          <NoResults>
            {searchTerm
              ? `No subscriptions found matching "${searchTerm}"`
              : "No subscriptions found"}
          </NoResults>
        );
      } else {
        return sortedSubscriptions.map((subscription, idx) => (
          <TableRow
            key={subscription.id || idx}
            columns="2fr 0.8fr 0.3fr 0.9fr 0.3fr 0.7fr 0.2fr 0.7fr 120px"
          >
            <DonorInfo>
              <DonorAvatar>
                {getInitials(subscription.getDisplayName())}
              </DonorAvatar>
              <DonorDetails>
                <DonorName>{subscription.getDisplayName()}</DonorName>
                <ContactInfo>
                  {getSubscriptionContactInfo(subscription).map(
                    (contact, index) => (
                      <ContactItem key={index}>{contact}</ContactItem>
                    )
                  )}
                </ContactInfo>
              </DonorDetails>
            </DonorInfo>

            <AmountValue style={{ textAlign: "right" }}>
              <strong>{formatCurrency(subscription.monthlyAmount)}</strong>{" "}
              <span style={{ fontWeight: "normal" }}>/ mo</span>
            </AmountValue>

            <div></div>

            <DateTimeValue>
              {formatDateOnly(subscription.nextDueDate)}
            </DateTimeValue>

            <div></div>

            <div>
              <DateTimeValue style={{ textAlign: "right" }}>
                {formatCurrency(subscription.totalPaid)} (
                {subscription.paymentCount})
              </DateTimeValue>
            </div>

            <div></div>

            <div>
              <StatusBadge status={subscription.status}>
                {subscription.status}
              </StatusBadge>
            </div>

            <ActionIcons>
              <TooltipContainer>
                <IconButton
                  onClick={() => handleViewSubscription(subscription)}
                  onMouseEnter={() =>
                    setHoveredTooltip(`view-sub-${subscription.id}`)
                  }
                  onMouseLeave={() => setHoveredTooltip(null)}
                >
                  <RefreshCcw size={14} />
                </IconButton>
                <Tooltip
                  visible={hoveredTooltip === `view-sub-${subscription.id}`}
                >
                  View
                </Tooltip>
              </TooltipContainer>
              {subscription.status === "Active" && (
                <TooltipContainer>
                  <IconButton
                    onClick={() => handleCancelSubscription(subscription)}
                    onMouseEnter={() =>
                      setHoveredTooltip(`cancel-sub-${subscription.id}`)
                    }
                    onMouseLeave={() => setHoveredTooltip(null)}
                  >
                    <XCircle size={14} />
                  </IconButton>
                  <Tooltip
                    visible={hoveredTooltip === `cancel-sub-${subscription.id}`}
                  >
                    Cancel
                  </Tooltip>
                </TooltipContainer>
              )}
            </ActionIcons>
          </TableRow>
        ));
      }
    }
  }

  return (
    <>
      {hasNoDonations ? (
        <EmptyState>
          <EmptyStateIcon>
            <DollarSign size={40} />
          </EmptyStateIcon>
          <EmptyStateTitle>No Donations</EmptyStateTitle>
          <EmptyStateText>
            There are no donations. Share the fundraiser to start getting
            donations.
          </EmptyStateText>
        </EmptyState>
      ) : (
        <>
          <TopDonationsContainer>
            <TopDonationsTitle>
              <DollarSign size={28} color="#10b981" />
              Top Donations
            </TopDonationsTitle>
            <TopDonationsGrid>
              {topDonations.map((donation) => (
                <DonationCard key={donation.id}>
                  <DonationAvatar isAnonymous={donation.isAnonymous}>
                    {getInitials(donation.getDisplayName())}
                  </DonationAvatar>
                  <DonationInfo>
                    <DonationName>{donation.getDisplayName()}</DonationName>
                    <DonationDate>
                      {formatDateOnly(donation.timestamp)}
                    </DonationDate>
                  </DonationInfo>
                  <DonationAmount>
                    {formatCurrency(donation.amount)}
                  </DonationAmount>
                </DonationCard>
              ))}
            </TopDonationsGrid>
          </TopDonationsContainer>

          <SectionHeaderWithSearch>
            <SectionTitle>
              {activeTab === "one-time"
                ? `Donations (${filteredDonations.length})`
                : `Subscriptions (${filteredSubscriptions.length})`}
            </SectionTitle>
            <SectionHeaderCenter>
              <DonationsSegmentControl>
                <DonationsSegmentButton
                  active={activeTab === "one-time"}
                  onClick={() => setActiveTab("one-time")}
                >
                  One Time
                </DonationsSegmentButton>
                <DonationsSegmentButton
                  active={activeTab === "recurring"}
                  onClick={() => setActiveTab("recurring")}
                >
                  Recurring
                </DonationsSegmentButton>
              </DonationsSegmentControl>
            </SectionHeaderCenter>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <SearchWrapper>
                <SearchInput
                  placeholder={
                    activeTab === "one-time"
                      ? "Search donations..."
                      : "Search subscriptions..."
                  }
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={clearSearch} title="Clear search">
                    <X size={16} />
                  </ClearButton>
                )}
              </SearchWrapper>
              <ExportDropDown onExport={exportGrid} isExporting={isExporting} />
            </div>
          </SectionHeaderWithSearch>

          <DataTable
            style={{
              minHeight:
                (activeTab === "one-time"
                  ? sortedDonations.length
                  : sortedSubscriptions.length) > 0
                  ? "500px"
                  : "auto",
            }}
          >
            {activeTab === "one-time" ? (
              <TableHeader columns="2.3fr 0.7fr 0.3fr 1.5fr 1.6fr 120px">
                <SortableHeader onClick={() => handleSort("name")}>
                  <span>Name</span>
                  {renderSortIcon("name")}
                </SortableHeader>
                <SortableHeader
                  /* justifyContent, not textAlign: SortableHeader is a flex container, so
                     text-align does not move its children - the label stayed left while the
                     right-aligned AmountValue sat under the far edge of the column. */
                  onClick={() => handleSort("donation")}
                  style={{ justifyContent: "flex-end" }}
                >
                  <span>Amount</span>
                  {renderSortIcon("donation")}
                </SortableHeader>
                <div></div>
                <SortableHeader
                  onClick={() => handleSort("date")}
                  style={{ marginLeft: "16px" }}
                >
                  <span>Date</span>
                  {renderSortIcon("date")}
                </SortableHeader>
                <div style={{ marginLeft: "40px", textAlign: "left" }}>
                  Payment Method
                </div>
                <div>Actions</div>
              </TableHeader>
            ) : (
              <TableHeader columns="2fr 0.8fr 0.3fr 0.9fr 0.3fr 0.7fr 0.2fr 0.7fr 120px">
                <SortableHeader onClick={() => handleSort("name")}>
                  <span>Name</span>
                  {renderSortIcon("name")}
                </SortableHeader>
                <SortableHeader
                  // Same flex/text-align mismatch as the Amount header above.
                  onClick={() => handleSort("recurring")}
                  style={{ justifyContent: "flex-end" }}
                >
                  <span>Recurring</span>
                  {renderSortIcon("recurring")}
                </SortableHeader>
                <div></div>
                <SortableHeader onClick={() => handleSort("nextDue")}>
                  <span>Next Due</span>
                  {renderSortIcon("nextDue")}
                </SortableHeader>
                <div></div>
                <SortableHeader
                  // Its figures are right-aligned, so the header follows them - same flex mismatch
                  // as the Recurring header above.
                  onClick={() => handleSort("paid")}
                  style={{ justifyContent: "flex-end" }}
                >
                  <span>Paid</span>
                  {renderSortIcon("paid")}
                </SortableHeader>
                <div></div>
                <div>Status</div>
                <div>Actions</div>
              </TableHeader>
            )}
            {renderGridsData()}
          </DataTable>
        </>
      )}

      <DonationDetailModal
        isVisible={showDetailModal}
        donation={selectedDonation}
        onClose={handleCloseDetailModal}
      />

      <RefundModal
        isVisible={showRefundModal}
        donation={refundDonation}
        onClose={(refresh?: boolean) => handleCloseRefundModal(refresh)}
      />

      <ViewSubscriptionModal
        isVisible={showViewSubscriptionModal}
        subscription={selectedSubscription}
        onClose={handleCloseViewSubscriptionModal}
      />

      <CancelSubscriptionModal
        isVisible={showCancelSubscriptionModal}
        subscription={selectedSubscription}
        onClose={handleCloseCancelSubscriptionModal}
        onConfirm={handleConfirmCancelSubscription}
      />
    </>
  );
};
